"use strict";

const ARCHIVE_SELECTOR = "[data-posts-archive]";
const SEARCH_INPUT_SELECTOR = "[data-posts-search-input]";
const TABS_SELECTOR = "[data-posts-tabs]";
const TAB_BUTTON_SELECTOR = "[data-posts-tab]";
const RESULTS_SELECTOR = "[data-posts-results]";
const EMPTY_SELECTOR = "[data-posts-empty]";
const GRID_SELECTOR = "[data-posts-news-grid]";
const SENTINEL_SELECTOR = "[data-posts-sentinel]";

const AJAX_ACTION_TAB = "posts_tab_action";
const AJAX_ACTION_LOAD = "posts_load_action";
const AJAX_ACTION_SEARCH = "posts_search_action";
const DEBOUNCE_MS = 300;
const SENTINEL_ROOT_MARGIN = "0px 0px 120px 0px";

function debounce(fn, delay) {
    let timer = null;

    return function (...args) {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => fn.apply(this, args), delay);
    };
}

function getScrollPosition() {
    const lenis = window.__lenis;

    if (lenis && typeof lenis.scroll === "number") {
        return lenis.scroll;
    }

    return window.scrollY;
}

function bindArchiveScroll(handler) {
    const lenis = window.__lenis;

    if (lenis && typeof lenis.on === "function") {
        lenis.on("scroll", handler);
        return () => lenis.off?.("scroll", handler);
    }

    window.addEventListener("scroll", handler, { passive: true });

    let attempts = 0;
    const maxAttempts = 120;

    const waitForLenis = () => {
        const instance = window.__lenis;

        if (instance && typeof instance.on === "function") {
            instance.on("scroll", handler);
            return;
        }

        attempts += 1;

        if (attempts < maxAttempts) {
            requestAnimationFrame(waitForLenis);
        }
    };

    requestAnimationFrame(waitForLenis);

    return () => window.removeEventListener("scroll", handler);
}

function resizeLenis() {
    if (window.__lenis && typeof window.__lenis.resize === "function") {
        window.__lenis.resize();
    }
}

async function fetchTab(tab) {
    const data = new FormData();
    data.append("action", AJAX_ACTION_TAB);
    data.append("nonce", window.WPURLS.nonce);
    data.append("tab", tab);

    const response = await fetch(window.WPURLS.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: data,
    });

    const payload = await response.json();

    return payload && payload.success ? payload.data : null;
}

async function fetchMore(page) {
    const data = new FormData();
    data.append("action", AJAX_ACTION_LOAD);
    data.append("nonce", window.WPURLS.nonce);
    data.append("page", String(page));

    const response = await fetch(window.WPURLS.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: data,
    });

    const payload = await response.json();

    return payload && payload.success ? payload.data : null;
}

async function fetchSearch(search) {
    const data = new FormData();
    data.append("action", AJAX_ACTION_SEARCH);
    data.append("nonce", window.WPURLS.nonce);
    data.append("search", search);

    const response = await fetch(window.WPURLS.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        body: data,
    });

    const payload = await response.json();

    return payload && payload.success ? payload.data : null;
}

function setEmptyState(emptyState, visible) {
    if (!emptyState) {
        return;
    }

    if (visible) {
        emptyState.removeAttribute("hidden");
        return;
    }

    emptyState.setAttribute("hidden", "");
}

function setTabState(archive, tabs, activeTab) {
    archive.dataset.postsActiveTab = activeTab;

    tabs.querySelectorAll(TAB_BUTTON_SELECTOR).forEach(function (button) {
        const isActive = button.getAttribute("data-posts-tab") === activeTab;
        button.setAttribute("aria-selected", String(isActive));
    });
}

function initPostsArchive() {
    const archive = document.querySelector(ARCHIVE_SELECTOR);
    const searchInput = document.querySelector(SEARCH_INPUT_SELECTOR);
    const tabs = document.querySelector(TABS_SELECTOR);
    const results = document.querySelector(RESULTS_SELECTOR);
    const emptyState = document.querySelector(EMPTY_SELECTOR);

    if (!archive || !searchInput || !tabs || !results || !window.WPURLS) {
        return;
    }

    let isSearchMode = false;
    let isLoading = false;
    let hasUserScrolled = false;
    let initialScrollPosition = getScrollPosition();
    let infiniteObserver = null;
    let sentinelWasIntersecting = false;

    function getCurrentPage() {
        return parseInt(archive.dataset.postsPage || "1", 10) || 1;
    }

    function setCurrentPage(page) {
        archive.dataset.postsPage = String(page);
    }

    function hasMore() {
        return archive.dataset.postsHasMore === "1";
    }

    function setHasMore(value) {
        archive.dataset.postsHasMore = value ? "1" : "0";
    }

    function canLoadMore() {
        return !isSearchMode
            && archive.dataset.postsActiveTab === "news"
            && hasMore()
            && !isLoading
            && hasUserScrolled;
    }

    function disconnectInfiniteObserver() {
        if (infiniteObserver) {
            infiniteObserver.disconnect();
            infiniteObserver = null;
        }

        sentinelWasIntersecting = false;
    }

    function resetSentinelState() {
        sentinelWasIntersecting = false;
    }

    function isSentinelNearViewport(sentinel) {
        const rect = sentinel.getBoundingClientRect();
        const margin = 120;

        return rect.top <= window.innerHeight + margin;
    }

    function tryLoadNextPage() {
        if (!canLoadMore()) {
            return;
        }

        if (sentinelWasIntersecting) {
            return;
        }

        const sentinel = results.querySelector(SENTINEL_SELECTOR);

        if (!sentinel || !isSentinelNearViewport(sentinel)) {
            return;
        }

        sentinelWasIntersecting = true;
        loadNextPage();
    }

    function bindInfiniteObserver() {
        disconnectInfiniteObserver();

        if (isSearchMode || archive.dataset.postsActiveTab !== "news" || !hasMore()) {
            return;
        }

        const sentinel = results.querySelector(SENTINEL_SELECTOR);

        if (!sentinel || !("IntersectionObserver" in window)) {
            return;
        }

        infiniteObserver = new IntersectionObserver(function (entries) {
            const entry = entries[0];

            if (!entry) {
                return;
            }

            if (!entry.isIntersecting) {
                sentinelWasIntersecting = false;
                return;
            }

            tryLoadNextPage();
        }, {
            root: null,
            rootMargin: SENTINEL_ROOT_MARGIN,
            threshold: 0,
        });

        infiniteObserver.observe(sentinel);
    }

    async function loadNextPage() {
        if (!canLoadMore()) {
            resetSentinelState();
            return;
        }

        const nextPage = getCurrentPage() + 1;
        isLoading = true;
        results.setAttribute("aria-busy", "true");

        try {
            const payload = await fetchMore(nextPage);

            if (!payload) {
                resetSentinelState();
                return;
            }

            const grid = results.querySelector(GRID_SELECTOR);

            if (payload.html && grid) {
                grid.insertAdjacentHTML("beforeend", payload.html);
            }

            setCurrentPage(nextPage);
            setHasMore(Boolean(payload.has_more));
            resizeLenis();

            requestAnimationFrame(function () {
                const sentinel = results.querySelector(SENTINEL_SELECTOR);

                if (sentinel && !isSentinelNearViewport(sentinel)) {
                    resetSentinelState();
                }

                bindInfiniteObserver();
            });
        } finally {
            isLoading = false;
            results.setAttribute("aria-busy", "false");
        }
    }

    function onScroll() {
        const currentScroll = getScrollPosition();
        const wasScrolled = hasUserScrolled;

        if (currentScroll > initialScrollPosition + 10) {
            hasUserScrolled = true;
        }

        if (!wasScrolled && hasUserScrolled) {
            tryLoadNextPage();
        }
    }

    async function loadTab(tab) {
        isLoading = true;
        isSearchMode = false;
        hasUserScrolled = false;
        initialScrollPosition = getScrollPosition();
        disconnectInfiniteObserver();
        results.setAttribute("aria-busy", "true");

        try {
            const payload = await fetchTab(tab);

            if (!payload) {
                return;
            }

            results.innerHTML = payload.html || "";
            setTabState(archive, tabs, payload.tab || tab);
            setCurrentPage(payload.page || 1);
            setHasMore(Boolean(payload.has_more));
            setEmptyState(emptyState, false);
            resizeLenis();
            bindInfiniteObserver();
        } finally {
            isLoading = false;
            results.setAttribute("aria-busy", "false");
        }
    }

    async function updateSearchResults() {
        const query = searchInput.value.trim();

        if (query === "") {
            isSearchMode = false;
            await loadTab(archive.dataset.postsActiveTab || "news");
            return;
        }

        isSearchMode = true;
        hasUserScrolled = false;
        initialScrollPosition = getScrollPosition();
        disconnectInfiniteObserver();
        isLoading = true;
        results.setAttribute("aria-busy", "true");

        try {
            const payload = await fetchSearch(query);

            if (!payload) {
                return;
            }

            results.innerHTML = payload.html || "";
            setHasMore(false);
            setEmptyState(emptyState, payload.count === 0);
        } finally {
            isLoading = false;
            results.setAttribute("aria-busy", "false");
        }
    }

    const debouncedSearch = debounce(updateSearchResults, DEBOUNCE_MS);

    tabs.addEventListener("click", function (event) {
        const button = event.target.closest(TAB_BUTTON_SELECTOR);

        if (!button || isLoading) {
            return;
        }

        const tab = button.getAttribute("data-posts-tab");

        if (!tab) {
            return;
        }

        if (searchInput.value.trim() !== "") {
            searchInput.value = "";
        }

        loadTab(tab);
    });

    searchInput.addEventListener("input", debouncedSearch);

    searchInput.addEventListener("search", function () {
        updateSearchResults();
    });

    bindArchiveScroll(onScroll);
    bindInfiniteObserver();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPostsArchive, { once: true });
} else {
    initPostsArchive();
}
